import React, { useEffect, useMemo, useState } from 'react';

import { RoyaltyModelsEnum } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import downloadLargeTokens from '@amzn/meridian-tokens/base/icon/download-large';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';

import { CellClassRules, CellEditRequestEvent, ColumnState, CsvExportParams, GridApi } from 'ag-grid-community';
import { cloneDeep, find } from 'lodash';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { FilterInput } from 'src/components/FilterInput';
import {
    CalcType,
    autoCalculateFinalRate,
    calcMultiplierName,
    calculateRateCalcType,
    calculateServiceRevenue,
    getCorePlanRate,
    getRateCalcTypeList,
    getRevenueCalcTypeList
} from 'src/components/RateCardDetail/ThirdPartyRates/ThirdPartyRatesAutoCalcUtil';
import { RateMultiplierDisplayName } from 'src/constants';
import { CORE_PLAN_COLUMN_TO_RATE_TYPE_MAP, MULTIPLIER_NAME_MAP } from 'src/constants/rateCards';
import 'src/styles/grid.scss';
import { IRateCardDetailStepProps, IThirdPartyRateGridRowData } from 'src/types/IRateCard';
import { getCurrentDate } from 'src/util/dateTimeUtil';
import { emptyCellFormatter } from 'src/util/grid/cellFormatter';
import { validateNumeric, validateRequired } from 'src/util/validationsUtil';

import { thirdPartyRatesColDefs } from './thirdPartyRatesColDefs';

export const ThirdPartyRates: React.FC<IRateCardDetailStepProps> = ({
    rateCardValues,
    setRateCardValues,
    isEditMode,
    multiplierTypeList,
    corePlanList,
    setIsNextDisabled,
    setIsPrevDisabled,
    dataLoaded
}: IRateCardDetailStepProps) => {
    const [filterText, setFilterText] = useState('');
    const [agGridApi, setAgGridApi] = useState<GridApi>();
    const [errors, setErrors] = useState<Set<string>>(new Set());
    const [warnings, setWarnings] = useState<Set<string>>(new Set());
    const [isPasting, setIsPasting] = useState(false);

    // Trigger validations
    useEffect(() => {
        // Only validate if done with pasting
        if (!isPasting) {
            validateThirdPartyRates();
        }
    }, [rateCardValues.thirdPartyData.thirdPartyRates, isPasting]);

    // Disable "Next" button if errors exist
    useEffect(() => {
        if (!setIsNextDisabled || !setIsPrevDisabled) return;
        setIsNextDisabled(errors.size > 0);
        setIsPrevDisabled(errors.size > 0);
    }, [errors]);

    const exportThirdPartyDiscountRates = () => {
        const params: CsvExportParams = {
            fileName: `3p_discount_rates_export_${getCurrentDate()}.csv`
        };
        agGridApi?.exportDataAsCsv(params);
    };

    const cellClassRules = useMemo<CellClassRules>(
        () => ({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            'error-cell': (params) => errors.has(generateErrorId(Number(params.node.id), params.colDef.field!)),
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            'warning-cell': (params) => warnings.has(generateErrorId(Number(params.node.id), params.colDef.field!))
        }),
        [errors, warnings]
    );

    const defaultColDef = {
        cellStyle: { borderRight: '1px solid #dde2eb' },
        wrapHeaderText: true,
        autoHeaderHeight: true,
        resizable: true,
        sortable: true,
        cellClassRules
    };

    const colDefs = [
        ...thirdPartyRatesColDefs,
        {
            headerName: 'Third party monthly price',
            groupId: 'thirdPartyMonthlyPrice',
            children: [
                {
                    field: 'endUserPrice',
                    headerName: 'End user',
                    filter: 'agNumberColumnFilter'
                },
                {
                    field: 'wholesalePrice',
                    headerName: 'Wholesale',
                    filter: 'agNumberColumnFilter'
                },
                {
                    field: 'thirdPartyCurrency',
                    headerName: '3rd party ccy',
                    filter: 'agSetColumnFilter',
                    valueFormatter: emptyCellFormatter
                }
            ]
        },
        {
            headerName: 'Rate card inputs',
            groupId: 'rateCardInputs',
            children: [
                {
                    field: 'corePlan',
                    headerName: 'Core plan',
                    filter: 'agSetColumnFilter',
                    valueFormatter: emptyCellFormatter,
                    editable: isEditMode,
                    cellEditor: 'agRichSelectCellEditor',
                    cellEditorPopup: true,
                    cellEditorParams: { values: corePlanList?.map((t) => t.corePlanDisplayName) }
                },
                {
                    field: 'corePlanPSM',
                    headerName: 'Core plan PSM',
                    filter: 'agNumberColumnFilter',
                    valueFormatter: emptyCellFormatter
                },
                {
                    field: 'rateCardCurrency',
                    headerName: 'Ratecard ccy',
                    filter: 'agSetColumnFilter',
                    valueFormatter: emptyCellFormatter
                },
                {
                    field: 'psmDiscCap',
                    headerName: 'PSM disc cap',
                    filter: 'agNumberColumnFilter',
                    valueFormatter: emptyCellFormatter
                },
                {
                    field: 'revDiscCap',
                    headerName: 'Rev disc cap',
                    filter: 'agNumberColumnFilter',
                    valueFormatter: emptyCellFormatter
                }
            ]
        },
        {
            headerName: 'Rate calculation',
            groupId: 'rateCalculation',
            children: [
                {
                    field: 'rateCalcType',
                    headerName: 'Calc type',
                    filter: 'agSetColumnFilter',
                    valueFormatter: emptyCellFormatter,
                    editable: isEditMode,
                    cellEditor: 'agRichSelectCellEditor',
                    cellEditorPopup: true,
                    cellEditorParams: (params: any) => {
                        const rateGroup = params.data.rateGroup;
                        return { values: getRateCalcTypeList(rateGroup) };
                    }
                },
                {
                    field: 'multiplierName',
                    headerName: 'Multiplier name',
                    filter: 'agSetColumnFilter',
                    valueFormatter: emptyCellFormatter,
                    editable: (params: any) => {
                        return params.data.rateCalcType === CalcType.Derived;
                    },
                    cellEditor: 'agRichSelectCellEditor',
                    cellEditorPopup: true,
                    cellEditorParams: {
                        values: multiplierTypeList
                            ?.filter((t) => MULTIPLIER_NAME_MAP.has(t.name || ''))
                            .map((t) => t.displayName)
                    }
                },
                {
                    field: 'multiplierValue',
                    headerName: 'Multiplier value',
                    filter: 'agNumberColumnFilter',
                    valueFormatter: emptyCellFormatter,
                    editable: (params: any) => {
                        return params.data.multiplierName === RateMultiplierDisplayName.CUSTOM;
                    }
                },
                {
                    field: 'rateType',
                    headerName: 'Rate type',
                    filter: 'agSetColumnFilter',
                    valueFormatter: emptyCellFormatter,
                    editable: (params: { data: { rateCalcType: string } }) =>
                        isEditMode && params.data.rateCalcType === CalcType.User,
                    cellEditor: 'agRichSelectCellEditor',
                    cellEditorPopup: true,
                    cellEditorParams: { values: [RoyaltyModelsEnum.PSM, RoyaltyModelsEnum.CPP] }
                },
                {
                    field: 'finalRate',
                    headerName: 'Final rate',
                    filter: 'agNumberColumnFilter',
                    valueFormatter: emptyCellFormatter,
                    editable: (params: any) => {
                        return (
                            params.data.rateCalcType === CalcType.User || params.data.rateType === RoyaltyModelsEnum.CPP
                        );
                    }
                }
            ]
        },
        {
            headerName: 'Revenue calculation',
            groupId: 'revenueCalculation',
            children: [
                {
                    field: 'revCalcType',
                    headerName: 'Calc type',
                    filter: 'agSetColumnFilter',
                    valueFormatter: emptyCellFormatter,
                    editable: isEditMode,
                    cellEditor: 'agRichSelectCellEditor',
                    cellEditorPopup: true,
                    cellEditorParams: { values: getRevenueCalcTypeList() }
                },
                {
                    field: 'serviceRev',
                    headerName: 'Service rev',
                    filter: 'agNumberColumnFilter',
                    valueFormatter: emptyCellFormatter,
                    editable: (params: any) => {
                        // Check if the 'revCalcType' value is 'someValue'
                        // and only then make this column editable
                        return params.data.revCalcType === CalcType.User;
                    }
                }
            ]
        }
    ];

    const rateGridSortModel: ColumnState[] = [{ colId: 'territory', sort: 'asc', sortIndex: 0 }];

    const updateMultiplierNameAndRelatedAttributes = (
        thirdPartyRate: IThirdPartyRateGridRowData,
        recalculateMultiplierName: boolean
    ) => {
        if (recalculateMultiplierName) {
            calcMultiplierName(thirdPartyRate);
        }
        const multiplierType = find(multiplierTypeList, { displayName: thirdPartyRate.multiplierName });
        const updatedMultiplierValue =
            //@ts-ignore
            rateCardValues.contractTerms.rateMultipliers[multiplierType?.contractTermMultiplierKey || ''];
        thirdPartyRate = {
            ...thirdPartyRate,
            multiplierTypeId: multiplierType?.rateMultiplierTypeId,
            multiplierValue:
                multiplierType?.displayName === RateMultiplierDisplayName.NONE ? '1' : updatedMultiplierValue
        };
        autoCalculateFinalRate(thirdPartyRate);
        return thirdPartyRate;
    };

    const onCellEditRequest = (event: CellEditRequestEvent<any>) => {
        const { column, newValue, node } = event;

        if (node.id === null || node.id === undefined) return;

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const rowId = Number(node.id);
        const newRowData = cloneDeep(rateCardValues.thirdPartyData.thirdPartyRates);

        if (column.getColId() === 'corePlan') {
            const newCorePlan = newValue;
            const newRateType = CORE_PLAN_COLUMN_TO_RATE_TYPE_MAP.get(newCorePlan) as string;
            const newCorePlanRate = getCorePlanRate(rateCardValues.coreRates, newRowData[rowId].territory, newCorePlan);
            newRowData[rowId] = {
                ...newRowData[rowId],
                corePlan: newCorePlan,
                corePlanId: find(corePlanList, { corePlanDisplayName: newValue })?.corePlanId,
                rateType: newRateType,
                corePlanPSM: newCorePlanRate
            };
            calculateRateCalcType(newRowData[rowId]);
            newRowData[rowId] = updateMultiplierNameAndRelatedAttributes(newRowData[rowId], true);
        } else if (column.getColId() === 'rateCalcType') {
            newRowData[rowId] = {
                ...newRowData[rowId],
                rateCalcType: newValue
            };
            newRowData[rowId] = updateMultiplierNameAndRelatedAttributes(newRowData[rowId], true);
        } else if (column.getColId() === 'multiplierName') {
            newRowData[rowId] = {
                ...newRowData[rowId],
                multiplierName: newValue
            };
            newRowData[rowId] = updateMultiplierNameAndRelatedAttributes(newRowData[rowId], false);
        } else if (column.getColId() === 'multiplierValue') {
            newRowData[rowId] = {
                ...newRowData[rowId],
                multiplierValue: newValue
            };
            autoCalculateFinalRate(newRowData[rowId]);
        } else if (column.getColId() === 'rateType') {
            newRowData[rowId] = {
                ...newRowData[rowId],
                rateType: newValue
            };
            calculateRateCalcType(newRowData[rowId]);
            newRowData[rowId] = updateMultiplierNameAndRelatedAttributes(newRowData[rowId], true);
        } else if (column.getColId() === 'revCalcType') {
            newRowData[rowId] = {
                ...newRowData[rowId],
                revCalcType: newValue
            };
            calculateServiceRevenue(newRowData[rowId]);
        } else if (column.getColId() === 'finalRate') {
            newRowData[rowId] = {
                ...newRowData[rowId],
                finalRate: newValue
            };
        } else if (column.getColId() === 'serviceRev') {
            newRowData[rowId] = {
                ...newRowData[rowId],
                serviceRev: newValue
            };
        }

        setRateCardValues((prevState) => {
            return {
                ...prevState,
                thirdPartyData: {
                    ...prevState.thirdPartyData,
                    thirdPartyRates: newRowData
                }
            };
        });
    };

    const validateThirdPartyRates = () => {
        clearWarningsAndErrors();

        rateCardValues.thirdPartyData.thirdPartyRates.forEach((rateRow, index) => {
            if (rateRow.rateCalcType === CalcType.SelectType) {
                addError(index, 'rateCalcType');
            }

            if (rateRow.rateCalcType === CalcType.Derived) {
                if (!validateRequired(rateRow.multiplierName?.toString())) {
                    addError(index, 'multiplierName');
                }
                if (!validateRequired(rateRow.multiplierValue?.toString())) {
                    addError(index, 'multiplierValue');
                }
            }

            if (
                !validateRequired(rateRow.finalRate?.toString()) ||
                !validateNumeric(rateRow.finalRate) ||
                Number(rateRow.finalRate) < 0
            ) {
                addError(index, 'finalRate');
            }

            if (
                !validateRequired(rateRow.serviceRev?.toString()) ||
                !validateNumeric(rateRow.serviceRev) ||
                Number(rateRow.serviceRev) < 0
            ) {
                addError(index, 'serviceRev');
            }

            if (Number(rateRow.finalRate) == 0) {
                addWarning(index, 'finalRate');
            }
        });
    };

    const addError = (row: number, column: string) => {
        setErrors((prevState) => {
            const newState = new Set(prevState);
            newState.add(generateErrorId(row, column));
            return newState;
        });
    };

    const addWarning = (row: number, column: string) => {
        setWarnings((prevState) => {
            const newState = new Set(prevState);
            newState.add(generateErrorId(row, column));
            return newState;
        });
    };

    const clearWarningsAndErrors = () => {
        setErrors(new Set());
        setWarnings(new Set());
    };

    const generateErrorId = (index: number, column: string) => {
        return `${index}-*-${column}`;
    };

    return (
        <Column height="100%" width="100%" minHeight={480} spacing="300">
            <Box type="fill" spacingInset="400">
                <Row alignmentHorizontal="justify">
                    <FilterInput filterText={filterText} setFilterText={setFilterText} />
                    <Button type="link" size="small" onClick={exportThirdPartyDiscountRates}>
                        <Icon tokens={downloadLargeTokens} />
                        Download
                    </Button>
                </Row>
            </Box>
            <BaseAgGrid
                defaultColDef={defaultColDef}
                columnDefs={colDefs}
                rowData={rateCardValues.thirdPartyData.thirdPartyRates}
                filterText={filterText}
                setAgGridApi={setAgGridApi}
                suppressSizeColumnsToFit={true}
                readOnlyEdit={true}
                singleClickEdit={false}
                onCellEditRequest={onCellEditRequest}
                suppressScrollOnNewData={true}
                onPasteStart={() => setIsPasting(true)}
                onPasteEnd={() => setIsPasting(false)}
                sortModel={rateGridSortModel}
                loading={!dataLoaded}
            />
        </Column>
    );
};
